// COP — the TOP banner STACK: the clearance-aware dual banner (the signature
// security furniture). CONTRACT:P4-COT-TDF-WRAPPER.1.0 / I2-SECURITY-LABELS.1.0.
//
// Two stacked rows, modeled on TDFBot's dual-banner IxD (ADAPTED to TDFLite — we
// drive it off the local PDP + the live KAS, not OpenTDF):
//
//   ROW 1 — THE QUERY LEVEL (the SUBJECT side). The level you're working AT =
//     min(your real clearance, the NPE you talk *through*, your voluntary
//     down-filter). The DOWN-FILTER DROPDOWN lives in the topmost-RIGHT here; the
//     whole row is colored by the query level. This is NOT the data ceiling — a
//     data ceiling would leak that higher content exists (violates Glomar). You can
//     only ever down-select to ≤ your clearance.
//   ROW 2 — THE AS-RENDERED classification (the DATA side). The DoD/IC overall
//     marking of what is ACTUALLY on screen at that query level (dynamic — elevates
//     to a decrypted inner P4 marking while shown). This is the compliance banner
//     line; it appears IDENTICALLY at the bottom edge (ClassificationBanner bottom).
//
// Down-selecting re-resolves the entire picture (host.setQueryLevel → the PDP runs
// with a lowered classification_level claim). The result is the same security thesis
// the persona switch proves, but voluntary and without re-login.

import { useState, type CSSProperties } from "react";
import type { HighWater, QueryScope, TimeWindowState } from "../host/cop.ts";
import type { DescopeSpec } from "../host/descope.ts";
import {
  bannerColor,
  bannerTextColor,
  bannerTier,
  bannerLine,
  capcoCaption,
  CAPCO_LINE_LABEL,
} from "./dod-marking.ts";
import { ViewAsPanel, type ViewAsOverlay } from "./ViewAsPanel.tsx";
import { useEscapeStack } from "./useEscapeStack.ts";

/** Compact step-button style for the recency control (◂ / ▸). Dimmed + non-interactive
 *  at the ladder ends. */
const timeBtnStyle = (disabled: boolean): CSSProperties => ({
  appearance: "none",
  border: "1px solid rgba(255,255,255,0.28)",
  borderRadius: 3,
  background: "transparent",
  color: "inherit",
  font: "inherit",
  lineHeight: 1,
  padding: "1px 4px",
  cursor: disabled ? "default" : "pointer",
  opacity: disabled ? 0.3 : 0.95,
});

export function BannerStack({
  highWater,
  queryScope,
  onQueryLevel,
  viewAs,
  onViewOverlay,
  timeWindow,
  onStepTime,
  kasOffline,
}: {
  highWater: HighWater;
  queryScope: QueryScope;
  onQueryLevel: (level: string) => void;
  /** The active view-as overlay (host-surfaced on status.queryScope.viewAs), or null. */
  viewAs: ViewAsOverlay | null;
  /** Apply/clear the dimensional descope overlay (host setViewOverlay seam). */
  onViewOverlay: (spec: DescopeSpec | null) => void;
  timeWindow: TimeWindowState;
  onStepTime: (dir: number) => void;
  /** Phase 2 Step E: when true, the KAS heartbeat has declared the KAS UNREACHABLE — render
   *  the "KAS OFFLINE — decrypt unavailable" strip. Protected content stays sealed at its outer
   *  marking (the host fails closed in decryptTdf); this is purely the operator-facing signal.
   *  Clears (strip disappears) when the KAS recovers. */
  kasOffline: boolean;
}): JSX.Element {
  // The VIEW-AS fly-out open state (summoned from the ROW-1 cue). Esc pops it (one level).
  const [panelOpen, setPanelOpen] = useState(false);
  useEscapeStack(() => setPanelOpen(false), panelOpen);
  const qLevel = queryScope.queryLevel;
  // ROW 1 renders the SUBJECT's FULL clearance marking at the query level — level + SCI
  // compartments + caveats (e.g. TOP SECRET//HCS/SI/SI-G/TK//NOFORN) — from queryHighWater,
  // colored by the same gold-on-TS//SCI tier rule as ROW 2. `qLevel` is still the bare
  // query level for the down-filter dropdown value + the narrowed check.
  const qHW = queryScope.queryHighWater;
  const qTier = bannerTier(qHW);
  const qColor = bannerColor(qTier);
  const qLabel = bannerLine(qHW); // full marking, e.g. TOP SECRET//HCS/SI/SI-G/TK//NOFORN
  // ROW 1 CAPCO caption: the SUBJECT-side ceiling (the query level you work AT). This
  // is min(clearance, NPE, down-filter) — NOT the data ceiling. Glomar-safe: it never
  // reveals that higher content exists; it only states how high you may currently look.
  const queryCaption = capcoCaption(CAPCO_LINE_LABEL.HIGHEST_POSSIBLE, qLabel);
  // ROW 2 CAPCO caption: the DATA-side overall marking of what is ACTUALLY on screen.
  const asRendered = bannerLine(highWater);
  const renderedCaption = capcoCaption(CAPCO_LINE_LABEL.AS_RENDERED, asRendered);
  // As-rendered color TIER: gold when TOP SECRET carries an SCI compartment (canon
  // TS//SCI), else the plain class color. Display-only; marking string unchanged.
  const arTier = bannerTier(highWater);
  const arColor = bannerColor(arTier);
  // The "narrowed" advisory state = the view differs from the operator's full clearance
  // for ANY reason: a down-selected level OR an active view-as overlay. A same-level,
  // multi-dimensional descope (e.g. releasability dropped) MUST still show advisory chrome
  // — an unmarked descoped view is a mis-marking hazard.
  const overlayActive = viewAs != null;
  const narrowed = qLevel !== queryScope.clearanceLevel || overlayActive;

  return (
    <div className="bannerstack bannerstack--top">
      {/* ROW 1 — query level (subject) + the down-filter dropdown, topmost-right. */}
      <div
        className="classbar classbar--query"
        // Raise the row's stacking context while the fly-out is open so the panel paints
        // above ROW-2 and the map (both z-index 30); overflow visible lets it drop below.
        style={{
          background: qColor,
          color: bannerTextColor(qTier),
          ...(panelOpen ? { zIndex: 200, overflow: "visible" } : null),
        }}
        role="status"
        aria-label={`${queryCaption}${narrowed ? " (narrowed)" : ""}`}
      >
        <span className="classbar__qtag" aria-hidden="true">
          {CAPCO_LINE_LABEL.HIGHEST_POSSIBLE}:
        </span>
        <span className="classbar__text">{qLabel}</span>
        {narrowed && (
          <span className="classbar__narrowed" aria-hidden="true">
            ▼ narrowed
          </span>
        )}

        {/* VIEW-AS trigger — the ROW-1 cue promoted into the panel summon. Reuses the
            global .downfilter box; carries an active accent while an overlay is live. */}
        <button
          type="button"
          className={`downfilter downfilter--trigger${overlayActive ? " downfilter--active" : ""}`}
          title="View as: voluntarily preview the picture at a lower ceiling / narrower releasability / dropped SCI+caveats / a named partner profile. Purely subtractive (Glomar-safe); the whole picture re-resolves and nothing above is revealed."
          aria-haspopup="dialog"
          aria-expanded={panelOpen}
          onClick={() => setPanelOpen((v) => !v)}
        >
          <span className="downfilter__cue" aria-hidden="true">
            VIEW AS
          </span>
          <span className="downfilter__caret" aria-hidden="true">
            ⌄
          </span>
        </button>
        {panelOpen && (
          <ViewAsPanel
            queryScope={queryScope}
            viewAs={viewAs}
            onQueryLevel={onQueryLevel}
            onViewOverlay={onViewOverlay}
            onClose={() => setPanelOpen(false)}
          />
        )}

        {/* RECENCY control — the two-button log-scale "show me the last N" window. A
            just-in-time pane-of-glass filter (the data is untouched); count == paint. */}
        <div
          className="timewindow"
          title="Recency: paint only contacts seen within this window. A just-in-time filter at the pane of glass — the data source of truth is untouched. ◂ narrows · ▸ widens (up to all time)."
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            marginLeft: 10,
            padding: "1px 6px",
            borderRadius: 4,
            background: "rgba(0,0,0,0.34)",
            font: "600 11px/1.4 ui-monospace, monospace",
            letterSpacing: "0.04em",
          }}
        >
          <span aria-hidden="true" style={{ opacity: 0.7 }}>
            SHOWN
          </span>
          <button
            type="button"
            aria-label="Narrow the recency window"
            disabled={timeWindow.stopIndex <= 0}
            onClick={() => onStepTime(-1)}
            style={timeBtnStyle(timeWindow.stopIndex <= 0)}
          >
            ◂
          </button>
          <span aria-live="polite" style={{ minWidth: 56, textAlign: "center" }}>
            {timeWindow.widthMs < 0 ? "all time" : `${timeWindow.live ? "last " : "@ "}${timeWindow.widthLabel}`}
          </span>
          <button
            type="button"
            aria-label="Widen the recency window"
            disabled={timeWindow.stopIndex >= timeWindow.stopCount - 1}
            onClick={() => onStepTime(1)}
            style={timeBtnStyle(timeWindow.stopIndex >= timeWindow.stopCount - 1)}
          >
            ▸
          </button>
        </div>
      </div>

      {/* ROW 2 — the as-rendered DoD/IC overall classification (matches the bottom). */}
      <div
        className="classbar classbar--top"
        style={{ background: arColor, color: bannerTextColor(arTier) }}
        role="status"
        aria-label={`top classification banner: ${renderedCaption}`}
      >
        <span className="classbar__cattag" aria-hidden="true">
          {CAPCO_LINE_LABEL.AS_RENDERED}:
        </span>
        <span className="classbar__text">{asRendered}</span>
      </div>

      {/* VIEWING-AS advisory strip — DISTINCT chrome (never restyles the CAPCO rows above,
          which keep their exact tier colors/strings). Present only while a view-as overlay
          is active; carries the target label, the STRICTER-than-target note (exactness), and
          a prominent one-click EXIT. Guards against an unmarked descoped view. */}
      {overlayActive && (
        <div
          className="classbar--viewas"
          role="status"
          aria-label={`Viewing as ${viewAs.label}${viewAs.exact === false ? " — preview is stricter than the real target" : ""}`}
        >
          <span className="classbar--viewas__tag" aria-hidden="true">
            VIEWING AS:
          </span>
          <span className="classbar--viewas__label">{viewAs.label}</span>
          {viewAs.exact === false && (
            <span className="classbar--viewas__strict">
              ▲ stricter than a real {viewAs.label}
            </span>
          )}
          <button
            type="button"
            className="classbar--viewas__exit"
            onClick={() => onViewOverlay(null)}
            aria-label="Exit view-as — return to your full clearance view"
          >
            EXIT
          </button>
        </div>
      )}

      {/* KAS-OFFLINE strip (Phase 2 Step E) — appears ONLY while the KAS heartbeat reports the
          KAS unreachable; clears the moment it recovers. Fail-closed signal: protected content
          is held SEALED at its outer marking (no rewrap attempted host-side), so decrypt is
          genuinely unavailable. This is an availability/link indicator, distinct from the
          classification rows above and from the FEED link tier (Cartouche). */}
      {kasOffline && (
        <div
          className="classbar classbar--kasoffline"
          role="alert"
          aria-label="KAS offline — decrypt unavailable; protected content shown at its outer marking only"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#7a1f12",
            color: "#ffd9cf",
            font: "700 11px/1.4 ui-monospace, monospace",
            letterSpacing: "0.06em",
            padding: "2px 10px",
            borderTop: "1px solid rgba(255,255,255,0.18)",
          }}
        >
          <span aria-hidden="true">⚠</span>
          <span>KAS OFFLINE — decrypt unavailable</span>
          <span aria-hidden="true" style={{ opacity: 0.75, fontWeight: 500 }}>
            · protected content shown at outer marking only
          </span>
        </div>
      )}
    </div>
  );
}
